import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Sparkles, Clock, Calendar } from "lucide-react";

export default function Activities() {
  const [bands, setBands] = useState([]);
  const [active, setActive] = useState("5-7");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/marketplace/age-bands").then((r) => setBands(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/activities/${active}`).then((r) => { setData(r.data); setLoading(false); });
  }, [active]);

  const bandMeta = bands.find((b) => b.id === active);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-3xl mb-8">
        <div className="overline mb-3">Age-based Activities</div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-brand-blue leading-tight">
          The right rhythm for every age.
        </h1>
        <p className="mt-4 text-lg text-brand-muted">
          Proven principles, daily activities, and a sample weekly schedule — designed for your child's stage of growth.
        </p>
      </div>

      {/* Band tabs */}
      <div className="flex flex-wrap gap-2 mb-10">
        {bands.map((b) => (
          <button
            key={b.id}
            data-testid={`activities-band-${b.id}`}
            onClick={() => setActive(b.id)}
            className={`px-5 py-3 rounded-2xl text-sm font-medium border-2 transition-all ${
              active === b.id
                ? "border-brand-green bg-brand-green-soft text-brand-blue"
                : "border-brand-border bg-white text-brand-blue hover:border-brand-blue"
            }`}
          >
            <div className="font-heading font-semibold">{b.label}</div>
            <div className="text-xs text-brand-muted">{b.phase}</div>
          </button>
        ))}
      </div>

      {loading || !data ? (
        <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-brand-green" /></div>
      ) : (
        <>
          {bandMeta && (
            <div className="rounded-3xl p-8 bg-gradient-to-br from-brand-blue to-brand-blue-hover text-white mb-10">
              <Sparkles size={24} className="text-brand-orange mb-3" />
              <div className="text-xs uppercase tracking-widest text-white/70">{bandMeta.phase}</div>
              <h2 className="font-heading text-3xl font-semibold mt-1">{bandMeta.label} — {bandMeta.focus}</h2>
            </div>
          )}

          {/* Principles */}
          <div className="surface p-7 mb-8">
            <h3 className="text-xl font-semibold text-brand-blue mb-4">Guiding principles</h3>
            <ul className="space-y-2.5">
              {data.principles.map((p, i) => (
                <li key={i} className="flex gap-3 text-brand-blue"><span className="text-brand-green font-bold">{i + 1}.</span> {p}</li>
              ))}
            </ul>
          </div>

          {/* Daily activities */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={20} className="text-brand-orange" />
              <h3 className="text-xl font-semibold text-brand-blue">Daily activities</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.daily_activities.map((a, i) => (
                <div key={i} data-testid={`activity-${i}`} className="surface p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-heading font-semibold text-brand-blue">{a.name}</div>
                    <span className="chip">{a.duration}</span>
                  </div>
                  <p className="text-sm text-brand-muted">{a.how}</p>
                  {a.resources?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-brand-border">
                      <div className="text-[10px] uppercase tracking-widest text-brand-green font-bold mb-1.5">Reference Learning Material</div>
                      <div className="flex flex-wrap gap-1.5">
                        {a.resources.map((r, ri) => (
                          <a key={ri} href={r.url} target="_blank" rel="noopener noreferrer"
                             data-testid={`activity-${i}-resource-${ri}`}
                             className="text-xs text-brand-blue hover:text-brand-green underline">
                            {r.type === "video" ? "🎥" : "📄"} {r.title}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Weekly schedule */}
          <div className="surface p-7 overflow-x-auto">
            <div className="flex items-center gap-2 mb-4">
              <Calendar size={20} className="text-brand-green" />
              <h3 className="text-xl font-semibold text-brand-blue">Sample weekly schedule</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-muted">
                  <th className="py-2 pr-4">Day</th>
                  <th className="py-2 pr-4">Morning</th>
                  <th className="py-2 pr-4">Afternoon</th>
                  <th className="py-2">Evening</th>
                </tr>
              </thead>
              <tbody>
                {data.sample_week.map((d, i) => (
                  <tr key={i} className="border-t border-brand-border">
                    <td className="py-3 pr-4 font-medium text-brand-blue">{d.day}</td>
                    <td className="py-3 pr-4 text-brand-muted">{d.morning}</td>
                    <td className="py-3 pr-4 text-brand-muted">{d.afternoon}</td>
                    <td className="py-3 text-brand-muted">{d.evening}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
